
import React from 'react';
import OnboardingLayout from '../../OnboardingLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { type } from 'os';

interface EditorWalkthroughProps {
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
}

const EditorWalkthrough: React.FC<EditorWalkthroughProps> = ({
  currentStep,
  totalSteps,
  onNext,
  onPrevious
}) => {
  return (
    <OnboardingLayout
      title="Editor Walkthrough"
      subtitle="Get to know the tools you'll use to create your content"
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={onNext}
      onPrevious={onPrevious}
    >
      <div className="max-w-4xl mx-auto">
        <Tabs defaultValue="write" className="w-full">
          <TabsList className="grid grid-cols-3 mb-6">
            <TabsTrigger value="write">Writing</TabsTrigger>
            <TabsTrigger value="format">Formatting</TabsTrigger>
            <TabsTrigger value="publish">Publishing</TabsTrigger>
          </TabsList>
          
          <TabsContent value="write" className="space-y-6">
            <div className="aspect-video bg-muted rounded-lg flex items-center justify-center border">
              <div className="text-center p-6">
                <p className="text-muted-foreground">Editor interface preview image</p>
              </div>
            </div>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-medium mb-4">Rich Text Editor</h3>
                <p className="text-muted-foreground mb-4">
                  Our editor gives you the perfect balance of simplicity and powerful features.
                  Start with a blank canvas and focus on your writing without distractions.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Distraction-free writing environment with autosave</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Support for images, videos, embeds, and other media</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Keyboard shortcuts for efficient writing and formatting</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>AI-powered writing suggestions and research assistant</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="format" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-lg font-medium mb-4">Text Formatting</h3>
                  <p className="text-muted-foreground mb-4">
                    Easily format your text with a wide range of styling options:
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      <span>Headings, bold, italic, underline, and strikethrough</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      <span>Bulleted and numbered lists</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      <span>Block quotes and code blocks</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      <span>Text alignment and indentation</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-lg font-medium mb-4">Media Integration</h3>
                  <p className="text-muted-foreground mb-4">
                    Enhance your articles with various media elements:
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      <span>Image uploads with captions and alignment</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      <span>Video embeds from YouTube, Vimeo, etc.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      <span>Social media embeds and tweets</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      <span>Interactive elements like polls and quizzes</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="publish" className="space-y-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-medium mb-4">Publishing Settings</h3>
                <p className="text-muted-foreground mb-4">
                  Control how your content is published and shared:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-base font-medium mb-2">Basic Settings</h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start">
                        <span className="text-primary mr-2">•</span>
                        <span>Schedule articles for future publishing</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-primary mr-2">•</span>
                        <span>Set article access (free or paid)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-primary mr-2">•</span>
                        <span>Add SEO metadata and tags</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-primary mr-2">•</span>
                        <span>Enable/disable comments</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="text-base font-medium mb-2">Advanced Features</h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start">
                        <span className="text-primary mr-2">•</span>
                        <span>Auto-generate audio versions</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-primary mr-2">•</span>
                        <span>Create article series and collections</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-primary mr-2">•</span>
                        <span>Set up email notifications for subscribers</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-primary mr-2">•</span>
                        <span>Cross-post to social media</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="text-sm font-medium mb-2">Ready to write?</h4>
              <p className="text-sm text-muted-foreground">
                After completing this onboarding, you'll have immediate access to the editor in your dashboard.
                Start with a draft to get familiar with all the tools before publishing your first article.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </OnboardingLayout>
  );
};

export default EditorWalkthrough;
